import { useState } from "react";
import Modal from "react-modal";
import Skeleton from "react-loading-skeleton";
import { searchProductsBySource, setProductMatch } from "../API/Backend";
import { showToast } from "./Toast";

Modal.setAppElement("#root");

const MODAL_STYLE = {
  overlay: { backgroundColor: "rgba(0,0,0,0.6)", zIndex: 1000 },
  content: {
    top: "50%", left: "50%", right: "auto", bottom: "auto",
    transform: "translate(-50%, -50%)",
    width: "min(1200px, 95vw)", maxHeight: "85vh",
    borderRadius: "12px", padding: "24px", overflowY: "auto",
  },
};

// Parse volume (ml) or weight (g) from title, handling pack multipliers.
// "180ml Pack of 6" → { qty: 1080, unit: "ml" }
// "1 Liter"         → { qty: 1000, unit: "ml" }
// "1.5kg"           → { qty: 1500, unit: "g" }
function parseQty(title) {
  const t = (title || "").toLowerCase();
  let qty = null;
  let unit = null;

  const mlM = t.match(/(\d+(?:\.\d+)?)\s*ml\b/);
  if (mlM) { qty = parseFloat(mlM[1]); unit = "ml"; }

  if (!qty) {
    const lM = t.match(/(\d+(?:\.\d+)?)\s*(?:l\b|lit(?:er|re)s?)/);
    if (lM) { qty = parseFloat(lM[1]) * 1000; unit = "ml"; }
  }

  if (!qty) {
    const kgM = t.match(/(\d+(?:\.\d+)?)\s*kg\b/);
    if (kgM) { qty = parseFloat(kgM[1]) * 1000; unit = "g"; }
  }

  if (!qty) {
    const gM = t.match(/\b(\d+(?:\.\d+)?)\s*g\b/);
    if (gM) { qty = parseFloat(gM[1]); unit = "g"; }
  }

  if (!qty) return null;

  const packM = t.match(/pack\s+(?:of\s+)?(\d+)/);
  if (packM) qty *= parseInt(packM[1]);

  return { qty, unit };
}

// "AED 6.01/L" or "AED 2.50/kg", null if unparseable
function perUnitLabel(title, price) {
  if (!price) return null;
  const p = parseQty(title);
  if (!p) return null;
  const per = (price / p.qty * 1000).toFixed(2);
  return `AED ${per}/${p.unit === "ml" ? "L" : "kg"}`;
}

function qtyDisplay(parsed) {
  if (!parsed) return null;
  if (parsed.qty >= 1000) {
    return `${(parsed.qty / 1000).toFixed(2)} ${parsed.unit === "ml" ? "L" : "kg"}`;
  }
  return `${parsed.qty}${parsed.unit}`;
}

// Remove size/quantity tokens from a title so cross-unit searches still find results.
// "Al Ain Full Cream Milk 1 Liter" → "Al Ain Full Cream Milk"
function stripQtyFromTitle(title) {
  return (title || "")
    .replace(/\d+(?:\.\d+)?\s*(?:ml|l\b|lit(?:er|re)s?|g\b|kg)\b/gi, "")
    .replace(/pack\s+(?:of\s+)?\d+/gi, "")
    .replace(/,?\s{2,}/g, " ")
    .trim();
}

export default function MatchModal({ visible, onClose, product }) {
  const [searchTerm, setSearchTerm] = useState(product?.title || "");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const oppositeSource = product?.type === "amazon" ? "carrefour" : "amazon";
  const oppositeLabel = oppositeSource === "carrefour" ? "Carrefour" : "Amazon";

  const sourcePrice = product?.price ?? product?.[`${product?.type}Price`];
  const sourceQty = parseQty(product?.title);
  const sourceUnitLabel = perUnitLabel(product?.title, sourcePrice);

  const handleOpen = () => {
    setSearchTerm(product?.title || "");
    setResults([]);
    setSelectedId(null);
    setHasSearched(false);
  };

  const handleSearch = () => {
    if (!searchTerm.trim()) return;
    setSearching(true);
    setResults([]);
    setHasSearched(true);
    setSelectedId(null);

    searchProductsBySource({
      first: 0,
      rows: 20,
      type: oppositeSource,
      filters: JSON.stringify({ global: { value: searchTerm.trim(), matchMode: "contains" } }),
    })
      .then(({ data }) => {
        setResults(data?.data?.products || []);
      })
      .catch((err) => showToast("error", "Search Failed", err.message))
      .finally(() => setSearching(false));
  };

  const handleConfirm = () => {
    if (!selectedId) return;
    setSaving(true);
    setProductMatch(product._id, selectedId)
      .then(({ data }) => {
        if (data?.success) {
          showToast("success", "Match Set", "Product matched successfully.");
          onClose(true);
        } else {
          showToast("error", "Failed", data?.message || "Could not set match.");
        }
      })
      .catch((err) => showToast("error", "Failed", err.message))
      .finally(() => setSaving(false));
  };

  if (!visible) return null;

  return (
    <Modal isOpen={visible} onAfterOpen={handleOpen} onRequestClose={() => onClose(false)} style={MODAL_STYLE}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 className="mb-1" style={{ fontWeight: 600 }}>Match with {oppositeLabel}</h5>
          <div style={{ fontSize: 13 }}>
            <strong>{product?.title}</strong>
            {sourcePrice ? <span className="ms-2 text-secondary">AED {sourcePrice}</span> : null}
            {sourceQty && <span className="ms-1 text-muted">({qtyDisplay(sourceQty)})</span>}
            {sourceUnitLabel && (
              <span
                className="ms-2 badge"
                style={{ background: "#e8f4fd", color: "#0d6efd", fontWeight: 500, fontSize: 12 }}
              >
                {sourceUnitLabel}
              </span>
            )}
          </div>
        </div>
        <button className="btn btn-sm btn-outline-secondary" onClick={() => onClose(false)}>✕</button>
      </div>

      {/* Search */}
      <div className="d-flex gap-2 mb-3">
        <input
          className="form-control"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          placeholder={`Search ${oppositeLabel} products…`}
        />
        <button className="btn btn-primary px-4" onClick={handleSearch} disabled={searching || !searchTerm.trim()}>
          {searching ? <span className="spinner-border spinner-border-sm" /> : <><i className="ri-search-line me-1" />Search</>}
        </button>
      </div>

      {/* Results */}
      <div style={{ overflowX: "auto" }}>
        <table className="table table-hover table-bordered align-middle" style={{ fontSize: 13 }}>
          <thead className="table-light">
            <tr>
              <th style={{ width: 80 }}>Image</th>
              <th>Title</th>
              <th style={{ width: 120 }}>Price</th>
              <th style={{ width: 150 }}>
                Per Unit
                {sourceUnitLabel && (
                  <div style={{ fontSize: 10, color: "#888", fontWeight: 400 }}>vs {sourceUnitLabel}</div>
                )}
              </th>
              <th style={{ width: 140 }}>Category</th>
              <th style={{ width: 60, textAlign: "center" }}>Select</th>
            </tr>
          </thead>
          <tbody>
            {searching ? (
              [...Array(5)].map((_, i) => (
                <tr key={i}>
                  {[80, "60%", 90, 130, 120, 50].map((w, j) => (
                    <td key={j}><Skeleton height={14} width={w} /></td>
                  ))}
                </tr>
              ))
            ) : results.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center text-muted py-4">
                  {hasSearched ? "No products found." : "Enter a search term above and press Search."}
                </td>
              </tr>
            ) : (
              results.map((p) => {
                const resultPrice = p.price ?? p[`${oppositeSource}Price`];
                const resultQty = parseQty(p.title);
                const resultUnitLabel = perUnitLabel(p.title, resultPrice) || p.pricePerUnit;

                let cheaperBadge = null;
                if (sourceQty && resultQty && sourceQty.unit === resultQty.unit && sourcePrice && resultPrice) {
                  const srcPer = sourcePrice / sourceQty.qty;
                  const resPer = resultPrice / resultQty.qty;
                  if (resPer < srcPer * 0.98) {
                    cheaperBadge = <span className="badge bg-success ms-1" style={{ fontSize: 10 }}>Cheaper</span>;
                  } else if (resPer > srcPer * 1.02) {
                    cheaperBadge = <span className="badge bg-danger ms-1" style={{ fontSize: 10 }}>Pricier</span>;
                  } else {
                    cheaperBadge = <span className="badge bg-secondary ms-1" style={{ fontSize: 10 }}>~Equal</span>;
                  }
                }

                return (
                  <tr
                    key={p._id}
                    onClick={() => setSelectedId(p._id)}
                    style={{
                      cursor: "pointer",
                      background: selectedId === p._id ? "#e8f4fd" : undefined,
                      outline: selectedId === p._id ? "2px solid #0d6efd" : undefined,
                    }}
                  >
                    <td>
                      <img
                        src={p.images?.[0]}
                        alt=""
                        height={50}
                        width={70}
                        style={{ objectFit: "cover", borderRadius: 4 }}
                        onError={(e) => { e.target.style.display = "none"; }}
                      />
                    </td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{p.title}</div>
                      {p.link && (
                        <a href={p.link} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: "#888" }} onClick={(e) => e.stopPropagation()}>
                          View on {oppositeLabel} ↗
                        </a>
                      )}
                      {p.matchedProductId && (
                        <div><span className="badge bg-warning text-dark mt-1" style={{ fontSize: 10 }}>Already matched</span></div>
                      )}
                    </td>
                    <td>
                      <strong>AED {resultPrice ?? "—"}</strong>
                      {resultQty && (
                        <div style={{ fontSize: 11, color: "#888" }}>{qtyDisplay(resultQty)}</div>
                      )}
                    </td>
                    <td>
                      {resultUnitLabel ? (
                        <div>
                          <span style={{ fontWeight: 500 }}>{resultUnitLabel}</span>
                          {cheaperBadge}
                        </div>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="text-muted" style={{ fontSize: 12 }}>
                      {p.categoryId?.title || p.category || "—"}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <input
                        type="radio"
                        name="match-select"
                        checked={selectedId === p._id}
                        onChange={() => setSelectedId(p._id)}
                        onClick={(e) => e.stopPropagation()}
                        style={{ width: 16, height: 16, cursor: "pointer" }}
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="d-flex justify-content-end gap-2 mt-2">
        <button className="btn btn-outline-secondary" onClick={() => onClose(false)}>Cancel</button>
        <button
          className="btn btn-primary"
          onClick={handleConfirm}
          disabled={!selectedId || saving}
        >
          {saving ? <><span className="spinner-border spinner-border-sm me-1" />Saving…</> : "Confirm Match"}
        </button>
      </div>
    </Modal>
  );
}
